// libs
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useLatest } from 'react-use';
import isEqual from 'lodash/isEqual';

// hooks
import useEvent from '@lyearn/core/hooks/useEvent';
// helpers
import {
  addList,
  getInputPlaceholderPlugin,
  getMarkType,
  isMarkActive,
  openLinkMarkMenu,
  selectionType,
  setSelection,
  toggleMark,
} from '@lyearn/lygo-blocks';
// components
import { EditorView, NewEditorProps } from '@lyearn/lygo-core';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import useAttachmentUpload from '@/features/messages/hooks/useAttachmentUpload';
import { instructionsEditorExtensions } from '@/lygo/InstructionEditor';
import useInputPreviews from '@/modules/corporate/modules/channel/views/ChannelDetails/hooks/useMessageInput/useInputPreviews';

import { MARK_DEFAULT_STATE, marks } from '../const';
import { editorLinkMark } from '../const';
// types
import { EditorContextProps, EditorOption, MarkState, UseEditorInputProps } from '../types';

const MAX_ALLOWED_FILES = 10;

const useEditor = ({
  value,
  placeholder,
  medias,
  onEditorBlur,
  onChangeText,
  onChangeMedias,
  onChangeMentions,
  charLimit,
  extensions = [],
  plugins = [],
  enableMentions,
  readOnly,
  editorRef,
  yProvider,
}: UseEditorInputProps): EditorContextProps & { remainingChar?: number } => {
  const onChangeTextRef = useLatest(onChangeText);
  const [editorView, setEditorView] = useState<EditorView | undefined>();
  const [marksActiveMap, setMarksActiveMap] =
    useState<Record<string, MarkState>>(MARK_DEFAULT_STATE);
  const [remainingChar, setRemainingChar] = useState<number | undefined>();
  const { mentions, updatePreviews } = useInputPreviews(value);

  useImperativeHandle(editorRef, () => ({ editorView }), [editorView]);

  useEffect(() => {
    onChangeMentions?.(Object.values(mentions));
  }, [mentions, onChangeMentions]);

  // This is required for initializing and registering existing files with uppy during component load
  const attachments = useMemo(
    () =>
      medias.map((media) => {
        return { media: { ...media } };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { uppy, files, removeFile } = useAttachmentUpload({
    attachments: attachments as any[],
    maxAllowedFiles: MAX_ALLOWED_FILES,
  });

  const filesArray = useMemo(() => Object.values(files), [files]);

  const onCreateEditorView = useCallback((view: EditorView) => {
    setEditorView(view);
  }, []);

  const markUpdateCallback = useCallback(() => {
    if (editorView) {
      const newMarkActiveMap = {} as Record<string, MarkState>;
      const { state } = editorView;
      marks.forEach((mark) => {
        if (mark === editorLinkMark.name) {
          const { selection } = state;
          const { empty } = selection;
          newMarkActiveMap[mark] = empty ? MarkState.Hidden : MarkState.Inactive;
        } else {
          newMarkActiveMap[mark] = isMarkActive(mark, state)
            ? MarkState.Active
            : MarkState.Inactive;
        }
      });
      setMarksActiveMap(newMarkActiveMap);
    }
  }, [editorView]);

  const onEditorChange = useEvent<NonNullable<NewEditorProps['onChange']>>((state, value) => {
    if (!editorView) {
      return;
    }

    const textLength = editorView.dom.textContent?.length;
    const isPlaceholder = value.length === 0;

    if (
      Number.isInteger(textLength) &&
      Number.isInteger(charLimit) &&
      textLength! >= charLimit! * 0.8
    ) {
      setRemainingChar(charLimit! - textLength!);
    } else {
      setRemainingChar(undefined);
    }
    const editorValue = isPlaceholder ? '' : value;
    onChangeTextRef.current?.(editorValue);
    markUpdateCallback();
    enableMentions && updatePreviews(editorView);
  });

  const onEditorFocus = useCallback(
    (ev: MouseEvent) => {
      if (editorView && !editorView.hasFocus() && ev.currentTarget === ev.target) {
        editorView.focus();

        // set selection to end of the editor
        const doc = editorView.state.doc;
        const position = doc.content.size;
        setSelection(editorView, selectionType.TEXT, position);
      }
      markUpdateCallback();
    },
    [editorView, markUpdateCallback],
  );

  const onEditorOptionClick = useCallback(
    (blockName: string, blockType: EditorOption) => {
      if (editorView) {
        if (blockName === editorLinkMark.name) {
          openLinkMarkMenu(editorView);
        } else {
          switch (blockType) {
            case EditorOption.List:
              addList(editorView, blockName);
              break;
            case EditorOption.Mark:
              const markType = getMarkType(blockName, editorView.state.schema);
              if (!markType) return;

              toggleMark(markType)(editorView.state, editorView.dispatch);
              break;
          }
        }
        editorView.focus();
        markUpdateCallback();
      }
    },
    [editorView, markUpdateCallback],
  );

  useEffect(() => {
    if (!isEqual(filesArray, medias)) {
      onChangeMedias?.(filesArray);
    }
  }, [filesArray, medias, onChangeMedias]);

  const onRemoveMedia = useCallback(
    (media: EntityMediaFragment) => {
      const localFile = files[media.mediaId];
      if (localFile) {
        removeFile(localFile);
      }
    },
    [files, removeFile],
  );

  return {
    editorProps: {
      readOnly,
      onChange: onEditorChange,
      onCreateEditorView,
      value,
      plugins: [...plugins, () => getInputPlaceholderPlugin(placeholder)],
      extensions: [...instructionsEditorExtensions, ...extensions],
      yProvider,
      onBlur: onEditorBlur,
    },
    onEditorFocus,
    uppy,
    onEditorOptionClick,
    mediaContainerProps: {
      onRemoveMedia,
      medias: filesArray,
    },
    marksActiveMap,
    remainingChar,
    editorView,
  };
};

export default useEditor;
